export const registrationTypes = [
  "Individual Registration",
  "Parent/Guardian Registering Children",
  "Grandparent Registering Grandchildren",
  "Legal Guardian Registering Children",
  "Foster Parent Registering Children",
] as const;

export const childCountOptions = ["0", "1", "2", "3", "4", "5", "More than 5"] as const;
export const relationshipOptions = ["Parent", "Grandparent", "Legal Guardian", "Foster Parent", "Other"] as const;
export const ageGroupOptions = ["Child", "Teen", "Adult", "Senior"] as const;
export const childAgeGroupOptions = ["Child", "Teen"] as const;
export const sexAtBirthOptions = ["Male", "Female"] as const;
export const raceOptions = ["White", "Black/African American", "American Indian or Alaskan Native", "Asian", "Native Hawaiian or Pacific Islander", "Other"] as const;
export const ethnicityOptions = ["Hispanic or Latino", "Not Hispanic or Latino", "Prefer Not to Answer", "Other (Please Specify)"] as const;

export type ChildRegistration = {
  childNumber: number;
  firstName: string;
  lastName: string;
  ageGroup: string;
  sexAtBirth: string;
  race: string;
  otherRace: string;
  ethnicity: string;
  otherEthnicity: string;
};

export type RegistrationFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  registrationType: string;
  numberOfChildren: string;
  relationshipToChild: string;
  children: ChildRegistration[];
  additionalChildrenInformation: string;
  totalHouseholdAttending: number;
  dateOfBirth: string;
  ageGroup: string;
  consentingAdultFullName: string;
  consentingAdultContactNumber: string;
  sexAtBirth: string;
  race: string;
  otherRace: string;
  ethnicity: string;
  otherEthnicity: string;
  addressLine1: string;
  unit: string;
  city: string;
  state: string;
  zipCode: string;
  householdSize: number | "";
  churchMember: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  selectedServices: string[];
  slotId: string;
  preferredLanguage: string;
  backToSchoolInterest: boolean;
  foodBagsInterest: boolean;
  generalConsent: boolean;
  emailConsent: boolean;
  smsConsent: boolean;
  registrationSource: string;
};

export function emptyChild(childNumber: number): ChildRegistration {
  return { childNumber, firstName: "", lastName: "", ageGroup: "Child", sexAtBirth: "Female", race: "", otherRace: "", ethnicity: "", otherEthnicity: "" };
}

export function requiredChildCount(value: string) {
  if (value === "More than 5") return 5;
  const count = Number(value);
  return Number.isInteger(count) ? Math.max(0, Math.min(5, count)) : 0;
}

export function createInitialRegistration(): RegistrationFormData {
  return {
    firstName: "", lastName: "", email: "", phone: "",
    registrationType: "Individual Registration", numberOfChildren: "0", relationshipToChild: "", children: [], additionalChildrenInformation: "", totalHouseholdAttending: 1,
    dateOfBirth: "", ageGroup: "Adult", consentingAdultFullName: "", consentingAdultContactNumber: "", sexAtBirth: "Female", race: "", otherRace: "", ethnicity: "", otherEthnicity: "",
    addressLine1: "", unit: "", city: "", state: "", zipCode: "", householdSize: "",
    churchMember: "", emergencyContactName: "", emergencyContactPhone: "",
    selectedServices: [], slotId: "", preferredLanguage: "EN", backToSchoolInterest: false, foodBagsInterest: false,
    generalConsent: false, emailConsent: true, smsConsent: false, registrationSource: "PRE_REGISTERED",
  };
}

export const formText = {
  EN: {
    steps: ["Personal", "Household", "Demographics", "Contacts", "Services", "Schedule"],
    sections: ["Personal information", "Household registration", "Demographics and address", "Church and emergency contact", "Health fair services", "Scheduling and consent"],
    required: "Required", optional: "Optional", children: "Children being registered", child: "Child", purpose: "Medical screening services", noPreference: "No preference", reviewPolicy: "I have read and agree to the RCC Mega Health Fair event communications consent, privacy acknowledgement, terms, and other agreement statements.",
  },
  ES: {
    steps: ["Personal", "Hogar", "Datos", "Contactos", "Servicios", "Horario"],
    sections: ["Información personal", "Registro del hogar", "Datos demográficos y dirección", "Iglesia y contacto de emergencia", "Servicios de la feria de salud", "Horario y consentimiento"],
    required: "Obligatorio", optional: "Opcional", children: "Niños que se registran", child: "Niño/a", purpose: "Servicios de evaluación médica", noPreference: "Sin preferencia", reviewPolicy: "He leído y acepto el consentimiento de comunicaciones, el aviso de privacidad, los términos y demás acuerdos de la Mega Feria de Salud RCC.",
  },
  FR: {
    steps: ["Personnel", "Foyer", "Profil", "Contacts", "Services", "Horaire"],
    sections: ["Informations personnelles", "Inscription du foyer", "Données démographiques et adresse", "Église et contact d’urgence", "Services de la foire de santé", "Horaire et consentement"],
    required: "Obligatoire", optional: "Facultatif", children: "Enfants inscrits", child: "Enfant", purpose: "Services de dépistage médical", noPreference: "Aucune préférence", reviewPolicy: "J’ai lu et j’accepte le consentement aux communications, l’avis de confidentialité, les conditions et les autres accords de la Mega Health Fair RCC.",
  },
} as const;
